// Utility to map airport codes to full information
import { airports } from '../data/mock';

/**
 * Get airport information by code
 * @param {string} code - Airport code (e.g., 'BOM')
 * @returns {object|null} - Airport object or null if not found
 */
export const getAirportByCode = (code) => {
  if (!code) return null;
  return airports.find(airport => airport.code === code.toUpperCase()) || null;
};

/**
 * Get airport city by code
 * @param {string} code - Airport code
 * @returns {string} - City name or code if not found
 */
export const getAirportCity = (code) => {
  const airport = getAirportByCode(code);
  return airport ? airport.city : code;
};

/**
 * Get airport name by code
 * @param {string} code - Airport code
 * @returns {string} - Airport name or code if not found
 */
export const getAirportName = (code) => {
  const airport = getAirportByCode(code);
  return airport ? airport.name : code;
};

/**
 * Transform offline flight segment to match Tripjack structure
 * @param {object} segment - Offline flight segment
 * @param {string} flightDate - The actual flight date (YYYY-MM-DD)
 * @returns {object} - Transformed segment
 */
export const transformOfflineSegment = (segment, flightDate = null) => {
  const fromAirport = getAirportByCode(segment.from_airport);
  const toAirport = getAirportByCode(segment.to_airport);
  
  // Use actual flight date or fallback to today
  const datePrefix = flightDate || new Date().toISOString().split('T')[0];
  
  return {
    id: `${segment.from_airport}_${segment.to_airport}_${segment.departure_time}`,
    
    // Departure airport
    da: {
      code: segment.from_airport,
      city: fromAirport?.city || segment.from_city || segment.from_airport,
      name: fromAirport?.name || segment.from_city || segment.from_airport,
      country: fromAirport?.country || 'Unknown',
      terminal: null
    },
    
    // Arrival airport
    aa: {
      code: segment.to_airport,
      city: toAirport?.city || segment.to_city || segment.to_airport,
      name: toAirport?.name || segment.to_city || segment.to_airport,
      country: toAirport?.country || 'Unknown',
      terminal: null
    },
    
    // Flight details
    fD: {
      aI: {
        code: segment.airline_code || '',
        name: segment.airline_name || 'Airline'
      },
      fN: segment.flight_number || '',
      eT: segment.aircraft_type || null
    },
    
    // Times (use actual flight date)
    dt: segment.departure_time ? `${datePrefix}T${segment.departure_time}:00` : null,
    at: segment.arrival_time ? `${datePrefix}T${segment.arrival_time}:00` : null,
    
    // Duration (convert "1h 30m" to minutes)
    duration: segment.duration ? parseDurationToMinutes(segment.duration) : 0,
    
    // Layover (connection time in minutes)
    cT: segment.layover ? parseDurationToMinutes(segment.layover) : null
  };
};

/**
 * Parse duration string to minutes
 * @param {string} durationStr - Duration like "1h 30m" or "2h 15m"
 * @returns {number} - Total minutes
 */
const parseDurationToMinutes = (durationStr) => {
  if (!durationStr) return 0;
  
  const hourMatch = durationStr.match(/(\d+)h/);
  const minuteMatch = durationStr.match(/(\d+)m/);
  
  const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
  const minutes = minuteMatch ? parseInt(minuteMatch[1]) : 0;
  
  return (hours * 60) + minutes;
};

/**
 * Enrich offline flight with proper airport and segment data
 * @param {object} flight - Offline flight object
 * @returns {object} - Enriched flight
 */
export const enrichOfflineFlight = (flight) => {
  if (!flight || flight.source !== 'offline') return flight;
  
  // Enrich main route
  const fromAirport = getAirportByCode(flight.from);
  const toAirport = getAirportByCode(flight.to);
  
  // Get the actual flight date (for segments)
  const flightDate = flight.flightDate || flight.date || flight.departureDate;
  
  const enriched = {
    ...flight,
    fromCity: fromAirport?.city || flight.fromCity || flight.from,
    toCity: toAirport?.city || flight.toCity || flight.to,
    fromAirportName: fromAirport?.name || flight.from,
    toAirportName: toAirport?.name || flight.to,
    // Ensure route object has departureDate
    route: {
      ...flight.route,
      from: flight.from || flight.route?.from,
      to: flight.to || flight.route?.to,
      departureDate: flightDate
    }
  };
  
  // Transform segments if it's a via flight
  if (flight.flightType === 'via' && flight.segments && flight.segments.length > 0) {
    enriched.segments = flight.segments.map(seg => transformOfflineSegment(seg, flightDate));
  } else if (flight.flightType === 'direct') {
    // For direct flights, create a single segment for consistency
    enriched.segments = [{
      id: `${flight.from}_${flight.to}_${flight.departureTime}`,
      da: {
        code: flight.from,
        city: fromAirport?.city || flight.fromCity || flight.from,
        name: fromAirport?.name || flight.from,
        country: fromAirport?.country || 'Unknown',
        terminal: null
      },
      aa: {
        code: flight.to,
        city: toAirport?.city || flight.toCity || flight.to,
        name: toAirport?.name || flight.to,
        country: toAirport?.country || 'Unknown',
        terminal: null
      },
      fD: {
        aI: {
          code: flight.airlineCode,
          name: flight.airline
        },
        fN: flight.flightNumber,
        eT: null
      },
      dt: flightDate && flight.departureTime ? `${flightDate}T${flight.departureTime}:00` : null,
      at: flightDate && flight.arrivalTime ? `${flightDate}T${flight.arrivalTime}:00` : null,
      duration: parseDurationToMinutes(flight.duration),
      cT: null
    }];
  }
  
  return enriched;
};
